export type Language = 'en' | 'fr';

export const SUPPORTED_LANGUAGES: { code: Language; label: string; nativeLabel: string }[] = [
  { code: 'en', label: 'English', nativeLabel: 'EN' },
  { code: 'fr', label: 'French', nativeLabel: 'FR' },
];

export function getLanguageFromPathname(pathname: string): Language {
  if (pathname.startsWith('/fr')) return 'fr';
  return 'en';
}

export function localePath(lang: Language, path: string): string {
  if (lang === 'fr') return `/fr${path === '/' ? '' : path}`;
  return path;
}

const translations = {
  en: {
    nav: {
      theDay: 'The Day',
      theme: 'Theme',
      rsvp: 'RSVP',
      accommodation: 'Accommodation and Activities',
      gallery: 'Gallery',
      gift: 'Gift',
      faq: 'FAQ',
    },
    landing: {
      dateLine: 'Saturday, 29th August 2026 · Dartmouth, Devon',
      scroll: 'Scroll',
      weddingOf: 'The Wedding of',
      welcomeText: 'We are so glad you are here. This is a private space for our invited guests, a place to find everything you need for a wonderful wedding weekend in Dartmouth.',
      ceremonyText: 'The ceremony will take place at St Petrox Church, followed by an afternoon reception at Gullsway Boathouse, and the most coincidental fireworks over the harbour.',
      enterSite: 'Enter Site',
      caption: 'St Petrox Church · Dartmouth Castle',
      ceremony: 'The Ceremony',
      ceremonyPlace: 'St Petrox Church',
      ceremonyNote: 'Dartmouth Castle · 12:00',
      photos: 'Refreshments and Photos',
      photosPlace: 'Castle Cove',
      photosNote: 'Post-ceremony · Afternoon',
      reception: 'The Reception',
      receptionPlace: 'Gullsway Boathouse',
      receptionNote: 'Dartmouth Waterfront · 15:00',
    },
    footer: {
      theDay: 'The Day',
      rsvp: 'RSVP',
      dartmouth: 'Dartmouth',
      gallery: 'Gallery',
      login: 'Login',
      privateNote: 'This is a private website for invited guests only. Dartmouth, Devon, England.',
    },
    schedule: {
      heroLabel: 'Guest area: The Weekend',
      heroTitle: '28–30 August 2026',
      rsvp: 'RSVP',
      runningOrder: 'Running Order',
      map: 'Map',
      guestDashboard: 'Guest Dashboard',
      runningOrderTitle: 'Running Order',
      pleaseNote: 'Please Note',
      regattaTitle: 'Dartmouth Royal Regatta',
      regattaText: "The wedding falls on the last day of Dartmouth Royal Regatta, a wonderful coincidence! Feel free to take the opportunity to explore what's going on in town between events throughout the day.",
      accommodationTitle: 'Accommodation and Travel, see specific sections for more details',
      accommodationText: "We'd recommend booking accommodation early as the town gets busy during Regatta week. If you're travelling by car, arriving the day before gives you the flexibility to use the passenger ferries or walk between venues on the day itself. Alternatively, the park and ride is a great option if you're driving in on the day.",
    },
    rsvp: {
      heroLabel: '',
      heroTitle: '',
      heroSubtitle: 'We would love to know if you can join us in Dartmouth.',
      formLabel: 'RSVP',
      formTitle: 'Will you join us?',
      formSubtitle: 'Please let us know if you are able to come, and share any dietary requirements so we can take care of you properly.',
      attendance: 'Attendance',
      joyfullyAccepts: 'Joyfully accepts',
      willBeThere: 'I will be there',
      regretfullyDeclines: 'Regretfully declines',
      cannotAttend: 'I cannot attend',
      attendanceRequired: 'Please let us know if you can attend',
      alreadySubmitted: 'Already submitted your RSVP?',
      enterDashboard: 'ENTER GUEST DASHBOARD',
    },
    dartmouth: {
      heroLabel: 'Your Guide to',
      heroTitle: 'Dartmouth, Devon',
      heroSubtitle: 'A small, beautiful town at the mouth of the River Dart. Everything you need to know for a wonderful weekend.',
      accommodationLabel: 'Where to Stay',
      accommodationTitle: 'Accommodation',
      accommodationSubtitle: 'The following are our personal recommendations but some might already be taken. Booking.com or airbnb are often the best approach for up-to-date availability.',
      moreOn: 'More on ExploreDartmouth.co.uk',
      thingsLabel: 'While You Are Here',
      thingsTitle: 'Things to Do',
      thingsSubtitle: "Dartmouth is one of England's most beautiful small towns. Here are our personal recommendations for making the most of your time here.",
      seeExplore: 'See & Explore',
      outdoors: 'Outdoors & Active',
    },
    faq: {
      heroLabel: 'George & Boadecea · XXIX · VIII · MMXXVI',
      heroTitle: 'Imagined Frequently Asked\nQuestions',
    },
    theme: {
      heroLabel: 'Boadecea & George · XXIX · VIII · MMXXVI',
      heroTitle: 'Theme: Shades of indigo',
      viewMoodboard: '→ View mood board',
    },
    honeymoon: {
      heroTitle: 'Honeymoon Fund',
      noGiftExpected: 'Truly, having you with us in Dartmouth on our wedding day is everything we could ask for. No gift is expected or required.',
      contributeText: 'However, if you would like to contribute to our honeymoon adventures, we would be deeply grateful. Since getting engaged in Japan we are dreaming of returning and have our sights set on a new province, embracing nature, new activities and of course the art and culture',
      arigato: 'Arigato gozaimasu!',
      amount: 'Amount',
      name: 'Your Name',
      email: 'Email (optional)',
      message: 'A message (optional)',
      contribute: 'Contribute',
      processing: 'Processing…',
      completeContribution: 'Complete Contribution',
      back: '← Back',
      securePayment: 'Secure Payment',
      thankYou: 'Thank you so much.',
      thankYouText: 'Your generosity will make our honeymoon all the more special.',
    },
    login: {
      location: 'Dartmouth, Devon · XXIX · VIII · MMXXVI',
    },
  },
  fr: {
    nav: {
      theDay: 'Le Jour',
      theme: 'Thème',
      rsvp: 'RSVP',
      accommodation: 'Hébergement et Activités',
      gallery: 'Galerie',
      gift: 'Cadeau',
      faq: 'FAQ',
    },
    landing: {
      dateLine: 'Samedi 29 août 2026 · Dartmouth, Devon',
      scroll: 'Défiler',
      weddingOf: 'Le Mariage de',
      welcomeText: 'Nous sommes si heureux que vous soyez là. Cet espace privé est réservé à nos invités, un endroit où trouver tout ce dont vous avez besoin pour un merveilleux week-end de mariage à Dartmouth.',
      ceremonyText: 'La cérémonie aura lieu à l\'église St Petrox, suivie d\'une réception en après-midi au Gullsway Boathouse, avec un feu d\'artifice des plus inattendus au-dessus du port.',
      enterSite: 'Accéder au site',
      caption: 'Église St Petrox · Château de Dartmouth',
      ceremony: 'La Cérémonie',
      ceremonyPlace: 'Église St Petrox',
      ceremonyNote: 'Château de Dartmouth · 12h00',
      photos: 'Rafraîchissements et Photos',
      photosPlace: 'Castle Cove',
      photosNote: 'Après la cérémonie · Après-midi',
      reception: 'La Réception',
      receptionPlace: 'Gullsway Boathouse',
      receptionNote: 'Front de mer de Dartmouth · 15h00',
    },
    footer: {
      theDay: 'Le Jour',
      rsvp: 'RSVP',
      dartmouth: 'Dartmouth',
      gallery: 'Galerie',
      login: 'Connexion',
      privateNote: 'Ce site est privé et réservé aux invités. Dartmouth, Devon, Angleterre.',
    },
    schedule: {
      heroLabel: 'Espace invités : Le Week-end',
      heroTitle: '28–30 août 2026',
      rsvp: 'RSVP',
      runningOrder: 'Programme',
      map: 'Carte',
      guestDashboard: 'Espace Invité',
      runningOrderTitle: 'Programme',
      pleaseNote: 'À noter',
      regattaTitle: 'Régate Royale de Dartmouth',
      regattaText: "Le mariage tombe le dernier jour de la Régate Royale de Dartmouth, une coïncidence merveilleuse ! N'hésitez pas à profiter de l'atmosphère de la ville entre les événements tout au long de la journée.",
      accommodationTitle: 'Hébergement et Transport, voir les sections spécifiques pour plus de détails',
      accommodationText: "Nous vous recommandons de réserver votre hébergement tôt car la ville est très animée pendant la semaine de la Régate. Si vous voyagez en voiture, arriver la veille vous donne la flexibilité d'utiliser les ferries passagers ou de marcher entre les lieux le jour même. Sinon, le parc-relais est une excellente option si vous conduisez le jour J.",
    },
    rsvp: {
      heroLabel: '',
      heroTitle: '',
      heroSubtitle: 'Nous serions ravis de savoir si vous pouvez nous rejoindre à Dartmouth.',
      formLabel: 'RSVP',
      formTitle: 'Serez-vous des nôtres ?',
      formSubtitle: 'Veuillez nous indiquer si vous pouvez venir et nous faire part de vos besoins alimentaires afin que nous puissions vous accueillir comme il se doit.',
      attendance: 'Présence',
      joyfullyAccepts: 'Accepte avec joie',
      willBeThere: 'Je serai présent(e)',
      regretfullyDeclines: 'Décline à regret',
      cannotAttend: 'Je ne pourrai pas venir',
      attendanceRequired: 'Veuillez nous indiquer si vous pouvez assister',
      alreadySubmitted: 'Vous avez déjà envoyé votre RSVP ?',
      enterDashboard: 'ACCÉDER À L\'ESPACE INVITÉ',
    },
    dartmouth: {
      heroLabel: 'Votre Guide de',
      heroTitle: 'Dartmouth, Devon',
      heroSubtitle: 'Une petite ville magnifique à l\'embouchure de la rivière Dart. Tout ce que vous devez savoir pour un merveilleux week-end.',
      accommodationLabel: 'Où Séjourner',
      accommodationTitle: 'Hébergement',
      accommodationSubtitle: 'Voici nos recommandations personnelles, mais certains établissements sont peut-être déjà complets. Booking.com ou Airbnb sont souvent les meilleures options pour vérifier les disponibilités.',
      moreOn: 'Plus sur ExploreDartmouth.co.uk',
      thingsLabel: 'Pendant Votre Séjour',
      thingsTitle: 'Activités',
      thingsSubtitle: "Dartmouth est l'une des plus belles petites villes d'Angleterre. Voici nos recommandations personnelles pour profiter au maximum de votre séjour.",
      seeExplore: 'Voir & Explorer',
      outdoors: 'Plein Air & Activités',
    },
    faq: {
      heroLabel: 'George & Boadecea · XXIX · VIII · MMXXVI',
      heroTitle: 'Questions Fréquemment\nImaginees',
    },
    theme: {
      heroLabel: 'Boadecea & George · XXIX · VIII · MMXXVI',
      heroTitle: 'L\'inspiration derrière la journée',
      viewMoodboard: '→ Voir le moodboard',
    },
    honeymoon: {
      heroTitle: 'Fonds de Lune de Miel',
      noGiftExpected: 'Vraiment, vous avoir avec nous à Dartmouth le jour de notre mariage est tout ce que nous pouvons demander. Aucun cadeau n\'est attendu ni requis.',
      contributeText: 'Cependant, si vous souhaitez contribuer à nos aventures de lune de miel, nous vous en serions profondément reconnaissants. Depuis nos fiançailles au Japon, nous rêvons d\'y retourner et avons nos regards tournés vers une nouvelle province, en embrassant la nature, de nouvelles activités et bien sûr l\'art et la culture.',
      arigato: 'Arigato gozaimasu !',
      amount: 'Montant',
      name: 'Votre nom',
      email: 'E-mail (facultatif)',
      message: 'Un message (facultatif)',
      contribute: 'Contribuer',
      processing: 'Traitement en cours…',
      completeContribution: 'Finaliser la contribution',
      back: '← Retour',
      securePayment: 'Paiement sécurisé',
      thankYou: 'Merci infiniment.',
      thankYouText: 'Votre générosité rendra notre lune de miel encore plus spéciale.',
    },
    login: {
      location: 'Dartmouth, Devon · XXIX · VIII · MMXXVI',
    },
  },
} as const;

export type Translations = typeof translations.en;

export function getTranslations(lang: Language): Translations {
  return translations[lang] as unknown as Translations;
}
